import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Center from 'raydiant-elements/layout/Center';
import CircularProgress from 'raydiant-elements/core/CircularProgress';
import { Popover } from '@headlessui/react';
import {
  ContentDetailByLocation,
  ContentDetailByPlaylist,
} from '@raydiant/api-client-js';

import { getContentDetailApiQueryParams } from '../AnalyticsFilter/utils';
import useContentDetail from '../../../hooks/useContentDetail';
import { selectUserProfile } from '../../../selectors/user';
import Page from '../../../components/Page';
import PageLayout from '../../../components/PageLayout/PageLayout';
import PageTitle from '../../../components/PageLayout/PageTitle';
import AnalyticsFilter from '../AnalyticsFilter/AnalyticsFilter';
import AnalyticsPageSkeleton from '../AnalyticsPageSkeleton';
import Empty from '../../../components/Empty/Empty';
import Error from '../../../components/Error/Error';
import PlaybackSummary from './PlaybackSummary';
import PlaylistsContentTable from './PlaylistsContentTable';
import { paginate, sort } from '../utils';
import { TableSort } from '../../../components/DataTable/DataTableHeader';
import LocationsContentTable from './LocationsContentTable';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import * as paths from '../../../routes/paths';
import EmptyStateImage from './assets/empty-state.svg';
import InactiveStateImage from './assets/inactive-state.svg';
import AverageContentChart from './AverageContentChart';
import AverageViewByDemographicChart from './AverageViewByDemographicChart';
import ContentViewByDemographicChart from './ContentViewByDemographicChart';
import VerifiedViewRateChart from './VerifiedViewRateChart';
import VerifiedViewsOverTimeChart from './VerifiedViewsOverTimeChart';
import ConversionFunnelChart from './ConversionFunnelChart';
import config from '../../../config';

const { supportAppUrl } = config;

export default function ContentDetailPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    const storedValue = localStorage.getItem('isSidebarOpen');
    return storedValue === 'true';
  });
  const [
    playlistsContentTableCurrentPage,
    setPlaylistsContentTableCurrentPage,
  ] = useState<number>(0);
  const [
    locationsContentTableCurrentPage,
    setLocationsContentTableCurrentPage,
  ] = useState<number>(0);

  const [playlistsContentTableSort, setPlaylistsContentTableSort] = useState<
    TableSort<ContentDetailByPlaylist>
  >({
    key: 'playbackDuration',
    direction: 'desc',
  });
  const [locationsContentTableSort, setLocationsContentTableSort] = useState<
    TableSort<ContentDetailByLocation>
  >({
    key: 'playbackDuration',
    direction: 'desc',
  });

  const query = getContentDetailApiQueryParams(search);
  const hasQueryParams = Object.values(query).length > 0;

  const [selectedContentId, setSelectedContentId] = useState<string>(
    query.presentation_id || '',
  );

  const { data, isError, isLoading } = useContentDetail(query);

  const currentUser = useSelector(selectUserProfile);

  const showEmptyState = !isLoading && !isError && !data;

  const showInactiveData =
    !isLoading &&
    !isError &&
    data &&
    !Object.values(data ?? {})?.flat()?.length;

  const showErrorState = !isLoading && isError;

  const showSkeleton = isLoading && !hasQueryParams;

  const showFilter = hasQueryParams;
  const showLoader = isLoading && hasQueryParams && !isError;

  const playlistsContentData = useMemo(() => {
    const sortedItems = sort<ContentDetailByPlaylist>(
      data?.contentDetailByPlaylist!,
      playlistsContentTableSort.key,
      playlistsContentTableSort.direction,
    );

    const paginatedData = paginate<ContentDetailByPlaylist>(sortedItems!, 10);

    return {
      data: paginatedData[playlistsContentTableCurrentPage]?.map(
        (item, index) => ({
          rank: index + 1,
          ...item,
        }),
      ),
      pageCount: paginatedData?.length,
    };
  }, [
    data?.contentDetailByPlaylist,
    playlistsContentTableSort,
    playlistsContentTableCurrentPage,
  ]);

  const locationsContentData = useMemo(() => {
    const sortedItems = sort<ContentDetailByLocation>(
      data?.contentDetailByLocation!,
      locationsContentTableSort.key,
      locationsContentTableSort.direction,
    );

    const paginatedData = paginate<ContentDetailByLocation>(sortedItems!, 10);

    return {
      data: paginatedData[locationsContentTableCurrentPage]?.map(
        (item, index) => ({
          rank: index + 1,
          ...item,
        }),
      ),
      pageCount: paginatedData?.length,
    };
  }, [
    data?.contentDetailByLocation,
    locationsContentTableSort,
    locationsContentTableCurrentPage,
  ]);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedValue = localStorage.getItem('isSidebarOpen');
      setIsSidebarOpen(storedValue === 'true');
    };

    window.addEventListener('isSidebarOpenEvent', handleStorageChange);

    return () => {
      window.removeEventListener('isSidebarOpenEvent', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    setPlaylistsContentTableCurrentPage(0);
    setLocationsContentTableCurrentPage(0);
  }, [isLoading]);

  useEffect(() => {
    if (selectedContentId !== query.presentation_id) {
      const searchParams = new URLSearchParams({
        presentation_id: selectedContentId,
        from: query.from,
        to: query.to,
        dateRangeKey: query.dateRangeKey,
        location_id: Array.isArray(query.location_id)
          ? query.location_id.join(',')
          : query.location_id || '',
        device_id: Array.isArray(query.device_id)
          ? query.device_id.join(',')
          : query.device_id || '',
      });

      history.push({
        pathname: paths.contentDetail(),
        search: selectedContentId ? `?${searchParams.toString()}` : '',
      });
    }
    // we only want to run this effect when selectedContentId is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContentId]);

  useEffect(() => {
    if (!query.presentation_id) {
      setSelectedContentId('');
    }
  }, [query.presentation_id]);

  if (!currentUser) {
    return (
      <Page>
        <PageLayout
          sectionClassName="min-h-full"
          title={
            <PageTitle
              title={t('analytics.overview')}
              subTitle={t('analytics.pageSubTitle')!}
            />
          }
        >
          <AnalyticsFilter className="mb-8" />
          <Center className="h-[500px]">
            <CircularProgress size={30}></CircularProgress>
          </Center>
        </PageLayout>
      </Page>
    );
  }

  return (
    <Page>
      <PageLayout
        sectionClassName="min-h-full"
        title={
          <PageTitle
            title={t('analytics.contentDetailPage.title')}
            subTitle={t('analytics.contentDetailPage.subTitle')!}
          />
        }
        isContentSelector
        setSelectedContentId={setSelectedContentId}
        selectedContentId={selectedContentId}
      >
        {showSkeleton && <AnalyticsPageSkeleton />}
        {showFilter && <AnalyticsFilter className="mb-8" />}
        {showLoader && (
          <Center className="h-[500px]">
            <CircularProgress size={30}></CircularProgress>
          </Center>
        )}
        {showEmptyState && (
          <div className="card bg-[#fdfbf5] w-full h-full items-center justify-center border-[24px] border-white">
            <Empty
              message={t('analytics.contentDetailPage.emptyPage.description')!}
              title={t('analytics.contentDetailPage.emptyPage.title')!}
              customImage={EmptyStateImage}
              wrapperClassName="md:gap-2 md:!max-w-[50rem]"
              imageClassName="lg:items-start md:h-full"
            ></Empty>
          </div>
        )}
        {showInactiveData && (
          <>
            <PlaybackSummary
              playbackSummaryData={data.contentDetailSummary[0] || {}}
              isSidebarOpen={isSidebarOpen}
              queryParams={query}
            />
            <div className="card bg-[#fdfbf5] w-full h-1/2 items-center justify-center border-[24px] border-white mt-7">
              <Empty
                message={
                  t('analytics.contentDetailPage.emptyData.description')!
                }
                title={t('analytics.contentDetailPage.emptyData.title')!}
                customImage={InactiveStateImage}
              >
                <a
                  href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('analytics.learnmore')}
                </a>
              </Empty>
            </div>
          </>
        )}
        {showErrorState && (
          <div className="card bg-[#fafafa] w-full h-full items-center justify-center">
            <Error>
              <a
                href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                rel="noreferrer"
                target="_blank"
              >
                {t('analytics.learnmore')}
              </a>
            </Error>
          </div>
        )}
        {!isError && data && Object.values(data).flat().length > 0 && (
          <>
            <div className="card">
              <PlaybackSummary
                playbackSummaryData={data.contentDetailSummary[0] || {}}
                isSidebarOpen={isSidebarOpen}
                queryParams={query}
              />
            </div>

            <div className="flex items-center mt-10 mb-8 ml-3">
              <h2 className="text-xl font-bold text-black mr-3">
                {t('analytics.contentDetailPage.performance.title')}
              </h2>
              <Popover className="relative">
                <Popover.Button>
                  <QuestionMarkIcon />
                </Popover.Button>
                <Popover.Panel className="absolute z-10 card popover">
                  <div className="overflow-hidden">
                    <p className="mb-3">
                      {t('analytics.contentDetailPage.performance.tooltip')}
                    </p>
                    <a
                      href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {t('analytics.learnmore')}
                    </a>
                  </div>
                </Popover.Panel>
              </Popover>
            </div>

            <div className="card mt-6">
              <>
                <div className="flex gap-3 items-center ml-8 mt-4">
                  <h2 className="text-xl font-bold text-black">
                    {data.contentDetailSummary[0]?.presentationName} vs{' '}
                    {t('analytics.contentDetailPage.averageContent.title')}
                  </h2>
                  <Popover className="relative">
                    <Popover.Button>
                      <QuestionMarkIcon />
                    </Popover.Button>
                    <Popover.Panel className="absolute z-10 card popover">
                      <div className="overflow-hidden">
                        <p className="mb-3">
                          {t(
                            'analytics.contentDetailPage.averageContent.tooltip',
                          )}
                        </p>
                        <a
                          href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {t('analytics.learnmore')}
                        </a>
                      </div>
                    </Popover.Panel>
                  </Popover>
                </div>
                {data.contentDetailSummary[0] && (
                  <AverageContentChart
                    contentData={data.contentDetailSummary[0]}
                  />
                )}
              </>
              {data.contentDetailByPlaylist[0] && (
                <PlaylistsContentTable
                  playlistsData={playlistsContentData.data}
                  presentationName={
                    data.contentDetailSummary[0]?.presentationName
                  }
                  currentPage={playlistsContentTableCurrentPage}
                  onPageChange={(page: number) => {
                    setPlaylistsContentTableCurrentPage(page);
                  }}
                  onSort={(sort) => {
                    setPlaylistsContentTableSort({
                      ...playlistsContentTableSort,
                      ...sort,
                    });
                    setPlaylistsContentTableCurrentPage(0);
                  }}
                  pageCount={playlistsContentData.pageCount}
                  sort={playlistsContentTableSort}
                  showPagination={data?.contentDetailByPlaylist.length > 10}
                />
              )}
              {data.contentDetailByLocation[0] && (
                <LocationsContentTable
                  locationsData={locationsContentData.data}
                  presentationName={
                    data.contentDetailSummary[0]?.presentationName
                  }
                  currentPage={locationsContentTableCurrentPage}
                  onPageChange={(page: number) => {
                    setLocationsContentTableCurrentPage(page);
                  }}
                  onSort={(sort) => {
                    setLocationsContentTableSort({
                      ...locationsContentTableSort,
                      ...sort,
                    });
                    setLocationsContentTableCurrentPage(0);
                  }}
                  pageCount={locationsContentData.pageCount}
                  sort={locationsContentTableSort}
                  showPagination={data?.contentDetailByLocation.length > 10}
                />
              )}
            </div>
            <>
              <div className="flex items-center mt-10 mb-8 ml-3">
                <h2 className="text-xl font-bold text-black mr-3">
                  {t('analytics.contentDetailPage.verifiedViews.title')}
                </h2>
                <Popover className="relative">
                  <Popover.Button>
                    <QuestionMarkIcon />
                  </Popover.Button>
                  <Popover.Panel className="absolute z-10 card popover">
                    <div className="overflow-hidden">
                      <p className="mb-3">
                        {t('analytics.contentDetailPage.verifiedViews.tooltip')}
                      </p>
                      <a
                        href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {t('analytics.learnmore')}
                      </a>
                    </div>
                  </Popover.Panel>
                </Popover>
              </div>
              <div className="card mt-6">
                {
                  <VerifiedViewsOverTimeChart
                    contentData={data.contentDetailByDay}
                    presentationName={
                      data.contentDetailSummary[0]?.presentationName || ''
                    }
                  />
                }
                {data.contentDetailSummary[0] && (
                  <ContentViewByDemographicChart
                    contentData={data.contentDetailSummary[0]}
                  />
                )}
              </div>
              <div className="flex items-center mt-10 mb-8 ml-3">
                <h2 className="text-xl font-bold text-black mr-3">
                  {t('analytics.contentDetailPage.conversion.title')}
                </h2>
                <Popover className="relative">
                  <Popover.Button>
                    <QuestionMarkIcon />
                  </Popover.Button>
                  <Popover.Panel className="absolute z-10 card popover">
                    <div className="overflow-hidden">
                      <p className="mb-3">
                        {t('analytics.contentDetailPage.conversion.tooltip')}
                      </p>
                      <a
                        href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {t('analytics.learnmore')}
                      </a>
                    </div>
                  </Popover.Panel>
                </Popover>
              </div>
              <div className="w-full flex gap-8 flex-col xl:flex-row mt-6">
                {data.contentDetailSummary[0] && (
                  <>
                    <div className="flex-1 xl:basis-5/12">
                      <ConversionFunnelChart
                        contentData={data.contentDetailSummary[0]}
                      />
                    </div>
                    <div className="flex-1 xl:basis-7/12">
                      <VerifiedViewRateChart
                        contentData={data.contentDetailSummary[0]}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="card mt-6">
                {data.contentDetailSummary[0] && (
                  <AverageViewByDemographicChart
                    contentData={data.contentDetailSummary[0]}
                  />
                )}
              </div>
            </>
          </>
        )}
      </PageLayout>
    </Page>
  );
}
